import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerCheckMenu, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { TopCallTimesData } from './top-call-times.model';
import { TopCallTimesService } from './top-call-times.service';
import { DatePipe } from '@angular/common';
import { UIGridTableState } from 'src/shared/models/ag-grid-helper.models';
import { GridOptions } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { Subscription } from 'rxjs';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CurrentAgentStatesService } from '../current-agent-states/current-agent-states.service';
import { AgentStateDetailComponent } from '../../modals/agent-state-detail/agent-state-detail.component';

@Component({
    selector: 'cv-top-call-times',
    templateUrl: './top-call-times.component.html',
    styleUrls: ['./top-call-times.component.scss']
})
export class TopCallTimesComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() filterValue: string = "";
    @ViewChild('topCallTimesGrid') agGrid: AgGridAngular;

    gridState: UIGridTableState;
    data: TopCallTimesData;
    dataLoading: boolean = false;
    dataLoadSuccess: boolean = false;
    isFirstLoad: boolean = true;
    gridReady: boolean = false;
    searchOpen: boolean = false;
    showFooter: boolean = true;
    selectedRow: boolean = null;
    locationVisible: boolean = false;
    stateSaveTimer: number = 0;
    restoreInProgress: boolean = false;
    subscriptions: Subscription[] = [];
	modalRef: NgbModalRef = null;

    //Dialog Helpers
    showDialog: boolean = false;
    dialogTargetAgent: boolean = null;
    displayCheckItems: Array<{ displayName: string, checked: boolean }>;

    public gridOptions: GridOptions = null;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private topCallTimesService: TopCallTimesService,
		private agentStateService: CurrentAgentStatesService,
		private agContextMenuService: AgContextMenuService,
		private navigationService: NavigationService,
		private ngbModal: NgbModal,
		public config: CvConfigurationService,
    ) { }

    ngOnInit(): void {
        this.initializeGrid();

        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => {
                return this.dataLoadSuccess;
            },
            onExport: (format: string) => {
                return this.topCallTimesService.LoadDataForExport(this.moduleKey, format);
            }
        });

        this.moduleManager.addMenuItem({
            name: "Columns",
            icon: "columns",
            getEnabled: () => {
                return this.gridOptions.columnDefs.length > 0;
            },
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => {
                return this.gridState?.columns;
            },
            getItemName: (i) => {
                var colState = this.gridState.columns[i];
                var column = this.agGrid?.api.getColumn(colState.name);

                if (column) // during column changes the state can have columns that are no longer in the list
                    return column.getColDef().headerName;
                else return '';
            },
            isItemChecked: (i) => {
                return this.gridState.columns[i].visible;
            },
            onCheckClicked: (i) => {
                this.toggleColumnVisible(i);
            }
        } as ModuleManagerCheckMenu);

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => {
                this.toggleOpenSearch();
            }
        });

        this.moduleManager.addMenuItem({
            name: "Display",
            icon: "legend",
            getEnabled: () => {
                return this.agGrid?.api && this.gridOptions.columnDefs.length > 0;
            },
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => {
                return this.displayCheckItems;
            },
            getItemName: (i) => {
                return this.displayCheckItems[i].displayName;
            },
            isItemChecked: (i) => {
                return this.displayCheckItems[i].checked;
            },
            onCheckClicked: (i) => {
                this.displayCheckItems[i].checked = !this.displayCheckItems[i].checked;
                this.displayCheckItemClicked(i);
            },
            readOnlyEnabled: true
        } as ModuleManagerCheckMenu);

        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: (stateData) => {
                if (!stateData) { // No grid state loaded
                    return;
                }

                if (typeof stateData === 'string')
                    this.gridState = JSON.parse(stateData);
                else if (stateData instanceof String)
                    this.gridState = JSON.parse(stateData.toString());
                else
                    this.gridState = stateData;

                //Location
                this.gridState.columns?.forEach(function (el) {
                    if (el.name == "Location") {
                        this.locationVisible = el.visible;
                        this.displayCheckItems[0].checked = this.locationVisible;
                    }
                });
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.registerEvents({
            onDashboardActivated: () => {
                if (this.agGrid?.api && this.gridReady) {
                    window.setTimeout(function () {
                        this.agGridHelperService.sizeColumns(this.gridOptions);
                    });
                }
            }
        });

        //Location
        if (this.gridOptions.columnDefs) {
            this.gridOptions.columnDefs.forEach(function (el) {
                if (el['field'] == "Location") {
                    this.locationVisible = !el['hide'];
                    return;
                }
            });
        }

        this.displayCheckItems = [
            {
                displayName: "Show Location",
                checked: this.locationVisible
            }
        ];

        this.displayCheckItemClicked = (i) => {
            switch (i) {
                case 0:
                    this.toggleLocationVisible();
                    break;
            }
        };
    }

    initializeGrid(): void {
        const hasWriteAccess = this.moduleManager.hasWriteAccess();
        this.gridOptions = this.agGridHelperService.createGridOptions({
            // default column options
            defaultColDef: {
                sortable: true,
                filter: hasWriteAccess,
                resizable: hasWriteAccess,
                suppressMovable: !hasWriteAccess,
                lockPinned: !hasWriteAccess,
                suppressMenu: !hasWriteAccess
            },

            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressColumnVirtualisation: true,

            context: { moduleManager: this.moduleManager },
            components: {
                stateCellRenderer: this.agGridHelperService.basicCellRenderer,
                agentNameRenderer: this.agGridHelperService.GroupDrillDownCellRenderer
            },

            // grid data
            columnDefs: [],
            rowData: [],

            // external filtering (our search bar)
            isExternalFilterPresent: () => {
                return !!this.filterValue;
            },
            doesExternalFilterPass: (node) => {
                return this.agGridHelperService.doesExternalFilterPass(node, this.filterValue);
            },

			// grid events
			onFirstDataRendered: (e) => {
				this.agGridHelperService.sizeColumnsSmart(e);
			},
			onGridReady: (e) => {
				this.gridReady = true;
				this.agGridHelperService.sizeColumnsSmart(e);
			},
			getRowId: ({ data: rowData }) => {
				return rowData.GroupID;
			},
			onGridSizeChanged: (e) => {
				if (e.clientWidth > 0) {
					this.agGridHelperService.sizeColumnsSmart(e);
				}
			},
			onSortChanged: (e) => {
				this.queueStateSave();
			},
			onColumnMoved: (e) => {
				this.queueStateSave();
			},
			onColumnPinned: (e) => {
				this.queueStateSave();
			},
			onColumnVisible: (e) => {
				this.agGridHelperService.sizeColumnsSmart(e);
				this.queueStateSave();
			},
		}, {
			enableCopy: true
		});

		this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));
	}

	openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any ) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(AgentStateDetailComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: AgentStateDetailComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

	displayCheckItemClicked(i): void {
		switch (i) {
			case 0:
				this.toggleLocationVisible();
				break;
		}
	}

    updateGridState(): void {
        if (!this.agGrid?.api || this.restoreInProgress)
            return;

        // generate state from grid
        var tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.gridState = tableState;
        }

        // tell module manager to save the state
        this.moduleManager.saveState('gridState');
    };

    async loadData(isAutoRefresh: boolean): Promise<void> {
        if (this.dataLoading)
            return; // don't have multiple outgoing requests
        this.dataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            let response = await this.topCallTimesService.LoadTopCallTimes(this.moduleKey, isAutoRefresh, this.isFirstLoad);

            if (response.status_code === 200) { // HTTP Success
                this.data = response.data;

                this.gridOptions.rowData.splice(0, this.gridOptions.rowData.length);
                this.gridOptions.columnDefs.splice(0, this.gridOptions.columnDefs.length);
                let datePipe = new DatePipe('en_US');

                //Add headers 
                for (i = 1; i < this.data.ColumnHeaders.length; i++) {

                    switch (this.data.ColumnHeaders[i]) {
                        case 'Location':
                            this.gridOptions.columnDefs.push({
                                headerName: this.data.ColumnHeaders[i],
                                field: this.data.ColumnHeaders[i],
                                filter: 'agTextColumnFilter',
                                hide: !this.locationVisible,
                                type: "groupColumn",
                            });
                            break;
                        case 'Call Date':
                            this.gridOptions.columnDefs.push({
                                field: this.data.ColumnHeaders[i],
                                headerName: this.data.ColumnHeaders[i],
                                cellRenderer: 'agAnimateShowChangeCellRenderer',
                                lockPosition: i <= 2 ? true : false,
                                valueFormatter: function (params) {
                                    return datePipe.transform(params.value, CVAngularDateFormat);
                                }
                            });
                            break;
                        case 'Call Time':
                            this.gridOptions.columnDefs.push({
                                field: this.data.ColumnHeaders[i],
                                headerName: this.data.ColumnHeaders[i],
                                cellRenderer: 'agAnimateShowChangeCellRenderer',
                                lockPosition: i <= 2 ? true : false,
                                valueFormatter: function (params) {
                                    return datePipe.transform(params.value, CVAngularTimeFormat);
                                }
                            });
                            break;
                        default:
                            this.gridOptions.columnDefs.push({
                                field: this.data.ColumnHeaders[i],
                                headerName: this.data.ColumnHeaders[i],
                                cellRenderer: 'agAnimateShowChangeCellRenderer',
                                lockPosition: i <= 2 ? true : false
                            });
                            break;
                    }
                }

                this.gridOptions.columnDefs[0]['type'] = 'groupColumn';
                this.gridOptions.columnDefs[0]['cellRenderer'] = 'agentNameRenderer';
                this.gridOptions.columnDefs[0]['field'] = 'Agent';

                // Map row data
                for (var i = 0; i < this.data.RowValues.length; i++) {
                    var row = this.data.RowValues[i];
                    var obj = {};
                    for (var j = 0; j < this.data.ColumnHeaders.length; j++) {
                        if (j == 1)
                            obj['Agent'] = row[j];
                        else
                            obj[this.data.ColumnHeaders[j]] = row[j];
                    }
                    this.gridOptions.rowData.push(obj);
                }

                if (this.agGrid?.api) {
                    this.agGrid?.api.setGridOption('rowData', this.gridOptions.rowData);

                    // check if columns changed
                    var numColumnsMatched = 0;
                    var newColumnFound = false;
                    var currentColumns = {};

                    // keep track of current columns
                    var allColumns = this.agGrid?.api.getColumns();
                    currentColumns = allColumns.reduce(function (dict, col) {
                        dict[col.getColId()] = true;
                        return dict;
                    }, {});

                    // compare new columns
                    this.gridOptions.columnDefs.forEach(function (colDef, colIndex) {
                        if (currentColumns[colDef['field']]) { // this column already existed
                            numColumnsMatched++;
                        } else { // new column
                            newColumnFound = true;
                        }
                    });

                    if (newColumnFound || numColumnsMatched < allColumns.length) {
                        // columns changed - do recalculations
                        this.agGrid?.api.setGridOption('columnDefs', this.gridOptions.columnDefs);
                        this.restoreColumnState();
                        this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api,);
                    }
                }
                this.updateGridState();
                this.dataLoadSuccess = true;
                this.isFirstLoad = false;
                this.moduleManager.setAutoRefreshReady();
            }
        }
        catch (e) { // HTTP Error
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        }
        finally {
            this.moduleManager.container.showDateRange = true;
            this.moduleManager.showLoader = false;
            this.dataLoading = false;
        }
    };

    toggleColumnVisible(i): boolean {
        // read visible state from table state (should always be up to date)
        var column = this.gridState.columns[i];
        var isHidden = !column.visible;

        //Location
        if (column.name == "parentGroupData") {
            this.displayCheckItems.forEach((el) => {
                if (el.displayName == "Show Location") {
                    el.checked = isHidden;
                    this.locationVisible = isHidden;
                    return;
                }
            });
        }

        // toggle column visibility
        this.agGrid?.api.setColumnVisible(column.name, isHidden);

        // update table state immediately to prevent UI delay (instead of using queue)
        this.updateGridState();
        return isHidden;
    };

    applyFilter(): void {
        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.onFilterChanged();
        }
    };

    clearFilter(): void {
        this.filterValue = "";
        this.applyFilter();
    };

    toggleOpenSearch(): void {
        if (this.searchOpen) {
            this.searchOpen = false;
            this.clearFilter();
        } else {
            this.searchOpen = true;
        }
    };

    clearGridState(): void {
        this.filterValue = "";

        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.onFilterChanged();
        }
    };

    toggleLocationVisible(): void {
        this.displayCheckItems.forEach((el) => {
            if (el.displayName == "Show Location") {
                this.locationVisible = el.checked;
                return;
            }
        })
        this.gridOptions.columnDefs.forEach((el) => {
            if (el.headerName == "Location") {
                el.suppressColumnsToolPanel = this.locationVisible;
            }
        })
        this.agGrid?.api.setColumnVisible('Location', this.locationVisible);
    };

    sizeColumns(): void {
        if (!this.data) {
            return;
        }

        // helper function to size columns
        this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions);
    };

    queueStateSave(): void {
        if (this.stateSaveTimer != 0) {
            window.clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = window.setTimeout(() => {
            this.updateGridState();
            this.stateSaveTimer = 0;
        }, 500);
    };

    restoreColumnState(): void {
        if (!this.gridState?.columns || !this.agGrid?.api) {
            return;
        }

        // keep state from being saved while restoring
        this.restoreInProgress = true;

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);

        // states can be saved again
        this.restoreInProgress = false;
    };
}
